import React from 'react';

const CalendarGrid = ({
  currentDate,
  view,
  selectedDate,
  appointments,
  filters,
  onAppointmentClick,
  onDayClick,
  onBackToMonth,
  getFilteredAppointments
}) => {
  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Get first day of month and calculate start date for grid
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());

  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const generateCalendarDays = () => {
    const days = [];
    
    for (let i = 0; i < 42; i++) { // 6 weeks × 7 days
      const cellDate = new Date(startDate);
      cellDate.setDate(startDate.getDate() + i);
      
      const dateStr = cellDate.toISOString().split('T')[0];
      const dayNum = cellDate.getDate();
      const isCurrentMonth = cellDate.getMonth() === month;
      const isToday = cellDate.toDateString() === today.toDateString();
      
      let cellClass = 'calendar-day-cell';
      if (!isCurrentMonth) cellClass += ' other-month';
      if (isToday) cellClass += ' today';
      
      const dayAppointments = getFilteredAppointments(dateStr);
      
      days.push({
        date: dateStr,
        dayNum,
        cellClass,
        appointments: dayAppointments,
        isCurrentMonth
      });
    }
    
    return days;
  };

  const getServiceClass = (serviceType) => {
    const service = serviceType.toLowerCase();
    if (service.includes('physiotherapy')) return 'physiotherapy';
    if (service.includes('massage')) return 'massage';
    if (service.includes('osteopathy')) return 'osteopathy';
    if (service.includes('acupuncture')) return 'acupuncture';
    if (service.includes('chiropractic')) return 'chiropractic';
    if (service.includes('consultation')) return 'consultation';
    return 'consultation'; // default
  };

  const renderAppointment = (appointment, index, dateStr) => (
    <div
      key={appointment.id}
      className={`calendar-appointment ${getServiceClass(appointment.type)} ${appointment.status}`}
      onClick={(e) => {
        e.stopPropagation();
        onAppointmentClick(appointment, dateStr);
      }}
      title={`${appointment.time} - ${appointment.patient} (${appointment.practitioner}) - ${appointment.type}`}
    >
      {appointment.time} {appointment.patient.split(' ')[0]}
    </div>
  );

  const renderDay = (day) => (
    <div
      key={day.date}
      className={day.cellClass}
      onClick={() => onDayClick(day.date)}
    >
      <div className="calendar-day-number">
        {day.dayNum}
      </div>
      <div className="calendar-appointments">
        {day.appointments.slice(0, 3).map((appointment, index) =>
          renderAppointment(appointment, index, day.date)
        )}
        {day.appointments.length > 3 && (
          <div className="more-appointments">
            +{day.appointments.length - 3} more
          </div>
        )}
      </div>
    </div>
  );

  if (view === 'month') {
    return (
      <div className="master-calendar">
        <div className="calendar-header-row">
          {weekdays.map(day => (
            <div key={day} className="calendar-header-cell">
              {day}
            </div>
          ))}
        </div>
        
        <div className="calendar-grid">
          {generateCalendarDays().map(day => renderDay(day))}
        </div>
      </div>
    );
  }

  // Week view (simplified implementation)
  if (view === 'week') {
    return (
      <div className="master-calendar">
        <div className="calendar-header-row">
          {weekdays.map(day => (
            <div key={day} className="calendar-header-cell">
              {day}
            </div>
          ))}
        </div>
        
        <div className="calendar-grid week-view">
          {generateCalendarDays().slice(0, 7).map(day => renderDay(day))}
        </div>
      </div>
    );
  }

  // Day view (enhanced implementation)
  if (view === 'day') {
    const viewDate = selectedDate ? new Date(selectedDate) : today;
    const viewDateStr = selectedDate || today.toISOString().split('T')[0];
    const dayAppointments = getFilteredAppointments(viewDateStr);

    return (
      <div className="day-view">
        <div className="day-view-header">
          <button
            className="btn btn-secondary day-view-back"
            onClick={onBackToMonth}
          >
            <span className="icon icon--arrow-left"></span>
            Back to Calendar
          </button>
          <h3 className="day-view-title">
            {viewDate.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </h3>
          <div className="day-view-stats">
            {dayAppointments.length} appointment{dayAppointments.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="day-view-appointments">
          {dayAppointments.length === 0 ? (
            <div className="no-appointments">
              <div className="no-appointments-icon">📅</div>
              <h4>No appointments scheduled</h4>
              <p>This day is free of appointments</p>
            </div>
          ) : (
            dayAppointments.map(appointment => (
              <div
                key={appointment.id}
                className={`day-appointment ${appointment.status}`}
                onClick={() => onAppointmentClick(appointment, viewDateStr)}
              >
                <div className="appointment-time">
                  <div className="time-label">{appointment.time}</div>
                  <div className="appointment-duration">60 min</div>
                </div>
                <div className="appointment-details">
                  <div className="appointment-patient">{appointment.patient}</div>
                  <div className="appointment-meta">
                    <span className="appointment-practitioner">{appointment.practitioner}</span>
                    <span className="appointment-separator"> • </span>
                    <span className="appointment-type">{appointment.type}</span>
                  </div>
                  <div className="appointment-location">
                    📍 {appointment.location === 'harley-street' ? 'Harley Street' :
                        appointment.location === 'canary-wharf' ? 'Canary Wharf' :
                        appointment.location === 'city-london' ? 'City of London' : appointment.location}
                  </div>
                </div>
                <div className={`appointment-status status-${appointment.status}`}>
                  {appointment.status}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default CalendarGrid;